import raceProblems from './raceProblems.json';
import supplementalProblems from './supplementalProblems';
import verifiedCourseProblems from './verifiedCourseProblems';
import courseProblems from './courseProblems';
import { getProblemQuality } from './problemQuality';
import { titleFromSlug } from '../utils/problemLinks';

export const languageOptions = [
  { key: 'python', label: 'Python', judge0Id: 100, mode: 'python' },
  { key: 'javascript', label: 'JavaScript', judge0Id: 102, mode: 'javascript' },
];

const problemAliases = new Map();
const allProblems = [...raceProblems, ...supplementalProblems, ...verifiedCourseProblems, ...courseProblems];

allProblems.forEach((problem) => {
  problemAliases.set(problem.id, problem.id);
  problem.aliases?.forEach((alias) => problemAliases.set(alias, problem.id));
});

function typeForLanguage(type, languageKey) {
  const maps = {
    cpp: {
      int: 'int',
      bool: 'bool',
      double: 'double',
      string: 'string',
      intArray: 'vector<int>',
      stringArray: 'vector<string>',
      intMatrix: 'vector<vector<int>>',
    },
    java: {
      int: 'int',
      bool: 'boolean',
      double: 'double',
      string: 'String',
      intArray: 'int[]',
      stringArray: 'String[]',
      intMatrix: 'int[][]',
    },
  };

  return maps[languageKey]?.[type] || 'int';
}

function defaultValue(type, languageKey) {
  const maps = {
    cpp: {
      int: '0',
      bool: 'false',
      double: '0.0',
      string: '""',
      intArray: '{}',
      stringArray: '{}',
      intMatrix: '{}',
    },
    java: {
      int: '0',
      bool: 'false',
      double: '0.0',
      string: '""',
      intArray: 'new int[] {}',
      stringArray: 'new String[] {}',
      intMatrix: 'new int[][] {}',
    },
  };

  return maps[languageKey]?.[type] || '0';
}

function starterCodeFor(problem, languageKey) {
  if (problem.starterCodeByLanguage?.[languageKey]) return problem.starterCodeByLanguage[languageKey];

  const signature = problem.signature || { args: [], returns: 'int' };
  const params = signature.args.map((arg) => arg.name).join(', ');

  if (languageKey === 'python') {
    return `from collections import Counter, defaultdict, deque
import bisect
import heapq
import itertools
import math


def solve(${params}):
    pass
`;
  }

  if (languageKey === 'javascript') {
    return `function solve(${params}) {
}

module.exports = { solve };
`;
  }

  if (languageKey === 'cpp') {
    const typedParams = signature.args.map((arg) => `${typeForLanguage(arg.type, 'cpp')} ${arg.name}`).join(', ');
    return `${typeForLanguage(signature.returns, 'cpp')} solve(${typedParams}) {
    // TODO
    return ${defaultValue(signature.returns, 'cpp')};
}
`;
  }

  const typedParams = signature.args.map((arg) => `${typeForLanguage(arg.type, 'java')} ${arg.name}`).join(', ');
  return `class Solution {
    public ${typeForLanguage(signature.returns, 'java')} solve(${typedParams}) {
        // TODO
        return ${defaultValue(signature.returns, 'java')};
    }
}
`;
}

function constraintsFor(problem) {
  if (problem.id === 'two-sum') {
    return [
      '2 <= nums.length <= 30000',
      '-1000000000 <= nums[i], target <= 1000000000',
      'Exactly one valid pair exists.',
      'Expected time: O(n). Quadratic solutions are rejected by hidden large tests.',
    ];
  }

  const sizeLimit = problem.difficulty === 'Hard' ? 10000 : problem.difficulty === 'Medium' ? 5000 : 1000;
  return problem.signature.args.map((arg) => {
    if (arg.type === 'int') return `${arg.name} fits in a signed 32-bit integer.`;
    if (arg.type === 'double') return `${arg.name} is a finite number.`;
    if (arg.type === 'bool') return `${arg.name} is true or false.`;
    if (arg.type === 'string') return `0 <= ${arg.name}.length <= ${sizeLimit}`;
    if (arg.type === 'intArray') return `0 <= ${arg.name}.length <= ${sizeLimit}; values fit in a signed 32-bit integer.`;
    if (arg.type === 'stringArray') return `0 <= ${arg.name}.length <= ${sizeLimit}; each string length is at most 100.`;
    if (arg.type === 'intMatrix') return `0 <= ${arg.name}.length <= ${sizeLimit}; values fit in a signed 32-bit integer.`;
    return `${arg.name} follows the problem input constraints.`;
  });
}

function generatedExplanation(problem, test) {
  if (problem.id === 'sqrt' || problem.aliases?.includes('sqrtx')) {
    return 'The answer is rounded down to the largest integer whose square does not exceed x.';
  }

  if (problem.id === 'two-sum') {
    return 'Return the two zero-based indices whose values add up to target.';
  }

  if (problem.id === 'add-two-numbers') {
    return 'The arrays encode linked-list digits in reverse order, so [2,4,3] represents 342.';
  }

  if (problem.id === 'merge-intervals') {
    return 'Overlapping ranges are collapsed into one interval while preserving sorted order.';
  }

  if (problem.id === 'valid-parentheses') {
    return 'Every closing bracket must match the most recent unmatched opening bracket.';
  }

  if (test.expected === true || test.expected === false) {
    return `This case should evaluate to ${test.expected ? 'true' : 'false'} under the stated condition.`;
  }

  return undefined;
}

function problemView(problem) {
  const quality = getProblemQuality(problem.id);
  return {
    ...problem,
    category: problem.category || 'Practice',
    quality,
    constraints: constraintsFor(problem),
    examplesText: problem.examples.map((example) => `Input: ${JSON.stringify(example.input)}\nOutput: ${JSON.stringify(example.output)}`),
    starterCode: Object.fromEntries(languageOptions.map((language) => [
      language.key,
      starterCodeFor(problem, language.key),
    ])),
    testCases: problem.tests.map((test, index) => ({
      name: `Test ${index + 1}`,
      input: test.input,
      expected: test.expected,
      explanation: test.explanation || (index === 0 ? generatedExplanation(problem, test) : undefined),
    })),
  };
}

export function getInternalProblem(slug) {
  const normalizedSlug = problemAliases.get(slug) || slug;
  const problem = allProblems.find((item) => item.id === normalizedSlug);
  if (problem) return problemView(problem);

  const title = titleFromSlug(slug);
  return problemView({
    id: slug,
    title,
    category: 'Reference',
    difficulty: 'Concept',
    prompt: 'This item is a reference lesson, not an executable coding prompt. Use the related course material or choose a practice problem from TopSWE100 to run code in the editor.',
    signature: { args: [], returns: 'int' },
    examples: [],
    tests: [],
  });
}

export { raceProblems };
